import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { StrategyMatchResult } from '../../types/signals';

interface StrategyBreakdownRowProps {
  result: StrategyMatchResult;
  isExpanded: boolean;
  onToggle: () => void;
}

function scoreBarColor(score: number): string {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-slate-500';
}

function DirectionBadge({ direction }: { direction: string }) {
  const styles: Record<string, string> = {
    BUY: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    SELL: 'bg-red-500/20 text-red-400 border-red-500/30',
    HOLD: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  const labels: Record<string, string> = { BUY: '🟢 BUY', SELL: '🔴 SELL', HOLD: '⚫ HOLD' };
  const s = styles[direction] ?? styles.HOLD;
  return (
    <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${s}`}>
      {labels[direction] ?? direction}
    </span>
  );
}

const StrategyBreakdownRow: React.FC<StrategyBreakdownRowProps> = ({
  result,
  isExpanded,
  onToggle,
}) => (
  <div className="rounded-lg border border-slate-700/40 overflow-hidden">
    <button
      onClick={onToggle}
      aria-label={result.strategy_name}
      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-800/40 transition-colors"
    >
      {/* Strategy name */}
      <span className="text-xs text-slate-300 truncate flex-1 min-w-0">
        {result.strategy_name}
      </span>
      {/* Score bar */}
      <div className="w-20 h-1.5 bg-slate-700 rounded-full overflow-hidden shrink-0">
        <div
          className={`h-full rounded-full ${scoreBarColor(result.match_score)}`}
          style={{ width: `${result.match_score}%` }}
        />
      </div>
      {/* Score % */}
      <span className="text-xs text-slate-400 w-8 text-right shrink-0">
        {result.match_score.toFixed(0)}%
      </span>
      {/* Direction badge */}
      <DirectionBadge direction={result.direction} />
      {/* Expand chevron */}
      {isExpanded ? (
        <ChevronUp size={12} className="text-slate-500 shrink-0" />
      ) : (
        <ChevronDown size={12} className="text-slate-500 shrink-0" />
      )}
    </button>

    {isExpanded && (
      <div className="px-3 pb-3 pt-1 bg-slate-800/20 border-t border-slate-700/40">
        <div className="flex flex-wrap gap-1.5">
          {result.patterns_fired.map((p) => (
            <span
              key={p}
              className="text-[11px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded px-1.5 py-0.5"
            >
              ✅ {p}
            </span>
          ))}
          {result.patterns_missed.map((p) => (
            <span
              key={p}
              className="text-[11px] bg-slate-700/30 text-slate-500 border border-slate-600/20 rounded px-1.5 py-0.5"
            >
              ◻ {p}
            </span>
          ))}
          {result.patterns_fired.length === 0 && result.patterns_missed.length === 0 && (
            <span className="text-[11px] text-slate-600">No pattern data</span>
          )}
        </div>
      </div>
    )}
  </div>
);

export default StrategyBreakdownRow;
